import { Box, Button, Card, CardContent, Divider, IconButton, MenuItem, Select, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { FC, MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';
import { OwnerMatchRuleDto } from '@/gen/api/securefile/api';
import OwnershipRuleDialog from './OwnershipRuleDialog';
import { ListContextProvider, useDeleteWithConfirmController, useGetMainList, useListContext } from 'ra-core';
import useLocalListController from '../list/useLocalListController';
import { RecPatch } from '@/dataProvider/tools';
import { Datagrid } from '../list/datagrid';
import { FunctionField } from '../field';
import { pages, sorts } from '@/utils/paramsWithGetMainList';
import { DataOwnerGroupResponseDto } from '@/gen/api/governance';
import { SimpleOption, SimpleOption2 } from '../governance/search/AmbientFacilitiesContext';
import { ClientDto } from '@/gen/api/user';
import CreateOrEdit from '@/controller/CreateOrEdit';
import { useDefaultSavedHandlers } from '@/controller/defaultSavedHandlers';
import Confirm from '../widgets/Confirm';
import WithSave from '@/controller/WithSave';
import Scrollbar from '../Scrollbar';



const resource = 'secure-files-ownership-rules'
const dataOwnerGroupRes = 'settings/governance/data-owner-group'
const securefileServerClientsRes = 'secure-files/clients/serveredition'

const genlistOptions = (x: MutableRefObject<Map<number, number>>): {sorters: Record<string, (a:OwnerMatchRuleDto & RecPatch, b:OwnerMatchRuleDto & RecPatch) => number>} => {
  return {
    sorters: {
      id: (a, b) => {
        const m = x.current;
        const ap = (m.get(a.ownerMatchId!) ?? a.priority ?? Number.MAX_SAFE_INTEGER)
        const bp = (m.get(b.ownerMatchId!) ?? b.priority ?? Number.MAX_SAFE_INTEGER)
        return ap < bp ? -1 : 1
      },
    }
  }
}

const OwnershipTab: FC = () => {
  const priorityChangesRef = useRef(new Map<number, number>())
  const listOptions = genlistOptions(priorityChangesRef)
  const listCtrl = useLocalListController({ resource, basePath: '', filter: { }, perPage: 9999 }, listOptions);
  const { data: dataOwnerGroupData, ids: ids1 } = useGetMainList(dataOwnerGroupRes, pages(), sorts(), {});
  const { data: securefileServerClientsData, ids: ids2 } = useGetMainList(securefileServerClientsRes, pages(), sorts(), {});

  const dataOwnerGroupOptions = useMemo(() => 
    (ids1 ?? []).map(id => {
      const g = dataOwnerGroupData![id] as any as DataOwnerGroupResponseDto
      return {...g, key: g.ownerGroupId!, text: g.name! } as SimpleOption
    })
    .sort((a, b) => a.text < b.text ? -1 : 1), 
    [dataOwnerGroupData, ids1])

  const securefileServerClientOptions = useMemo(() => 
    (ids2 ?? []).map(id =>{
      const c = securefileServerClientsData![id] as any as ClientDto
      return {...c, key: c.clientId!, text: c.clientName! } as SimpleOption2
    })
    .sort((a, b) => a.text < b.text ? -1 : 1),
    [ids2, securefileServerClientsData])

  return (
    <ListContextProvider value={listCtrl}>
      <OwnershipTabBody dataOwnerGroupOptions={dataOwnerGroupOptions} securefileServerClientOptions={securefileServerClientOptions} priorityUpdates={priorityChangesRef} />
    </ListContextProvider>
  )
}
const OwnershipTabBody: FC<{
  dataOwnerGroupOptions: SimpleOption[],
  securefileServerClientOptions: SimpleOption2[],
  priorityUpdates: MutableRefObject<Map<number, number>>
}> = ({
  dataOwnerGroupOptions,
  securefileServerClientOptions,
  priorityUpdates,
}) => {
  const listContext = useListContext()
  const { loading, ids, data, refetch, setFilters, displayedFilters, setSort } = listContext;

  const [states, setStates] = useState({
    selectedServer: null as ClientDto|null,
    dialogOpen: false,
    editingRule: null as OwnerMatchRuleDto | null,
    deletingRule: null as OwnerMatchRuleDto&RecPatch | null,
    priorityUpdates: new Map<number, number>(),
  })
  type S = typeof states
  const mutInTickRef = useRef(null as null|[string, (x:S)=>S][])
  const setStateMut = useCallback((k: string, setAct: (x:S)=>S) => {
    if (!mutInTickRef.current) {
      mutInTickRef.current = []
      setTimeout(() => {
        const es = new Set(['handleServerChange', 'handleAddRule', 'handleEditRule', 'deletingRule'])
        setStates(v => {
          let v1 = v
          let shouldResetPUMap = false
          for (const [k, fn] of mutInTickRef.current ?? []) {
            shouldResetPUMap = shouldResetPUMap || es.has(k)
            v1 = fn(v1)
          }
          if (shouldResetPUMap && v1.priorityUpdates.size > 0) {
            v1.priorityUpdates = new Map()
            priorityUpdates.current = v1.priorityUpdates
            setTimeout(() => {
              setSort('id') // force localListController rerender
            })
          }
          mutInTickRef.current = null
          return v1
        })
      })
    }
    mutInTickRef.current!.push([k, setAct])
  }, [priorityUpdates, setSort])

  const handleServerChange = (event: { target: { value: string } }) => {
    const client : ClientDto|null = 
      event.target.value === '' ? null :
        securefileServerClientOptions.find(x => x.key === event.target.value) as any
    setStateMut('handleServerChange', v => ({...v, selectedServer: client }))
    setFilters({ computer: client?.clientName }, displayedFilters)
  };

  const handleAddRule = () => {
    setStateMut('handleAddRule', v => {
      if (!v.selectedServer) return v
      return {...v, dialogOpen: true, editingRule: null }
    })
  };
  
  const handleEditRule = (rule: OwnerMatchRuleDto) => {
    setStateMut('handleEditRule', v => {
      if (!v.selectedServer) return v
      return {...v, dialogOpen: true, editingRule: rule }
    })
  };
  
  const delHandlers = useDefaultSavedHandlers('Delete Ownership Rule')
  delHandlers.onSuccess.merge(useCallback(() => {
    refetch?.()
  }, [refetch]))
  const tempDelRec = useRef(null as OwnerMatchRuleDto|null)

  const {
    open: delConfirmOpen,
    handleDialogOpen,
    handleDialogClose: handleDeleteDialogClose,
    handleDelete,
    loading: delSaving,
  } = useDeleteWithConfirmController({
    resource,
    record: states.deletingRule ?? undefined,
    mutationMode: 'pessimistic',
    ...delHandlers,
  });

  const handleMove = async (index: number, up: boolean) => {
    if (states.selectedServer || states.deletingRule || states.editingRule || states.dialogOpen) { return }
    // boundry check
    if (
      (up && index === 0) ||
      (!up && index === ids.length - 1)
    ) return;
    const rules = (ids ?? []).map(id => data[id]) as OwnerMatchRuleDto[];
    const d = up ? -1 : 1
    const swapbuddy = rules[index + d];
    const current = rules[index];
    const m = states.priorityUpdates
    const c0 = m.get(current.ownerMatchId!) ?? current.priority!
    const s0 = m.get(swapbuddy.ownerMatchId!) ?? swapbuddy.priority!
    m.set(current.ownerMatchId!, s0);
    m.set(swapbuddy.ownerMatchId!, c0);

    // compact the map
    const rsSorted = rules.sort((a, b) => a.priority! < b.priority! ? -1 : 1)
    const range = rsSorted.reduce((acc, r, i) => {
      const p1 = m.get(r.ownerMatchId!);
      if (p1 != null && p1 !== r.priority) {
        if (acc.length === 0) {
          acc.push(i)
        } else {
          acc[1] = i
        }
      }
      return acc
    }, [] as number[])
    let m1 = m
    if (range.length === 2) {
      m1 = new Map(
        rsSorted.slice(range[0], range[1] + 1).map(r => [r.ownerMatchId!, m.get(r.ownerMatchId!) ?? r.priority!])
      )
    } else {
      m1 = new Map()
    }

    setStates(v => ({...v, priorityUpdates: m1 }))
    priorityUpdates.current = m1
    setSort('id') // localListController rerender

    // assumption: priorityUpdates map should live only within lifetime of the current effective rules set
    //    in other words, if any rule edit happens (be it add/update/delete/re-order), 
    //    priorityUpdates map should be reset, and local list should be refetched
    // setStateMut() function will help to ensure
  };
  const clearPriorityUpdates = () => {
    setStateMut('handleServerChange', (v) => v)
  }
  const handleSavePriorityUpdates = (save: (x: Record<number, number>) => Promise<void>) => async () => {
    if (states.priorityUpdates.size === length) { return }
    try {
      const payload = [...states.priorityUpdates.entries()].reduce((acc, pair) => ({...acc, [pair[0]]: pair[1]}), {})
      await save(payload)
      refetch?.()
      const m1 = new Map()
      setStates(v => ({...v, priorityUpdates: m1 }))
      priorityUpdates.current = m1
    } catch (error: any) {
      // ...
    }
  }
  const handlers = useDefaultSavedHandlers('Save Priority Changes')

  const handleEditDialogClose = () => {
    setStates(v => {
      return {...v, dialogOpen: false, editingRule: null }
    })
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="textSecondary">Server:</Typography>
            <Select
              // label="Server"
              value={states.selectedServer?.clientId ?? ''}
              onChange={handleServerChange}
              size="small"
              sx={{ minWidth: 240 }}
              inputProps={{ 'aria-label': 'Server selector' }}
            >
              <MenuItem value={''}><i><Typography variant="caption" color="text.secondary">all</Typography></i></MenuItem>
              {securefileServerClientOptions.map(s => (
                <MenuItem key={s.key} value={s.key}>{s.text}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ ml: 'auto' }} />
          {states.priorityUpdates.size > 0 &&
            <>
              <CreateOrEdit resource="secure-files-ownership-rules" basePath='' record={{ id: 'multiple-reprioritize' }} id="multiple-reprioritize" {...handlers}>
                <WithSave async render={({save, saving}) =>
                  <>
                    <Button size="small" sx={{ mr: 2 }} disabled={saving} onClick={clearPriorityUpdates}>Cancel</Button>
                    <Button size="small" variant="outlined"
                      disabled={saving}
                      onClick={handleSavePriorityUpdates(save)}
                    >
                      Save Priority Changes
                    </Button>
                  </>
                } />
                <Divider sx={{ mx: 2 }} orientation="vertical" flexItem variant='middle' />
              </CreateOrEdit>
            </>}
          <Button variant="contained" startIcon={<AddIcon />} size="small"
            disabled={!states.selectedServer}
            onClick={handleAddRule} 
          >
            Add Rule
          </Button>
        </Stack>

        {loading ? (
          <Skeleton variant="rectangular" height={200} />
        ) : ids.length === 0 ? (
          <Typography color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
            {states.selectedServer
              ? 'No rules configured for this server. Click Add Rule to begin.'
              : 'No rules configured. Select a server then click Add Rule to begin.'}
          </Typography>
        ) : (
          <Scrollbar options={{ suppressScrollY: true}}>

            <Box sx={{ minWidth: 1150 }}>
              <Datagrid 
                hasBulkActions={false} 
                {...listContext}
                sx={{ 
                  '& th' : { py: 2 },
                  '& td' : { py: 2 },
                  '& tr div.MuiBox-root': {
                    // '& div.encryptedDbTip': { top: theme => theme.spacing(-5) },
                    // '& span.encryptedDbTipText': { top: theme => theme.spacing(-5) },
                  },
                  '& .rule-folder': { maxWidth: 700, display: 'inline-block', wordBreak: 'break-word' },
                }}
              >
                <FunctionField label="Priority" sortable={false}
                  render={(d?:OwnerMatchRuleDto&RecPatch) => (
                    !d ? null :
                    <Typography variant="body2" fontWeight="bold" 
                      color={states.priorityUpdates.has(d.ownerMatchId ?? -1) ? "primary" : undefined} 
                    >
                      #{states.priorityUpdates.get(d.ownerMatchId!) ?? d?.priority}
                    </Typography>
                  )}
                />
                <FunctionField label="Folder" sortable={false} className="rule-folder"
                  render={(d?:OwnerMatchRuleDto&RecPatch) => (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      <Typography variant="caption" sx={{ 
                        px: 0.5, py: 0.25, mr: 0.5, borderRadius: '4px',
                        bgcolor: theme => theme.palette.info.main, 
                        color: theme => theme.palette.info.contrastText,
                      }}>
                        {d?.computer}
                      </Typography>
                      <br/>{d?.pathPrefix}
                    </Typography>
                  )}
                />
                <FunctionField label="Data Owner Group" sortable={false}
                  render={(d?:OwnerMatchRuleDto&RecPatch) => {
                    const ogroup = dataOwnerGroupOptions.find(g => g.key === d?.dataOwnerGroupId)
                    return (
                      <Typography variant="body2">{ogroup?.text ?? 'N/A'}</Typography>
                    )
                  }}
                />
                <FunctionField label="Action" sortable={false}
                  render={(d?:OwnerMatchRuleDto&RecPatch) => {
                    if (!d) { return null }
                    const index = ids.findIndex(id => id === d.id)
                    return (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {!states.selectedServer &&
                          <>
                            <Tooltip title="Move up">
                              <IconButton size="small" onClick={() => handleMove(index, true)} color="secondary" disabled={index === 0}>
                                <ArrowUpwardIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Move down">
                              <IconButton size="small" onClick={() => handleMove(index, false)} color="secondary" disabled={index === ids.length - 1}>
                                <ArrowDownwardIcon fontSize="small" /></IconButton></Tooltip>
                          </>}
                        {states.selectedServer &&
                          <Tooltip title="Edit">
                            <IconButton size="small" color="secondary" onClick={() => handleEditRule(d)}>
                              <EditIcon fontSize="small" /></IconButton></Tooltip>}
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" 
                            disabled={states.priorityUpdates.size > 0}
                            onClick={(e) => {
                              setStateMut('deletingRule', v => ({...v, deletingRule: d}))
                              tempDelRec.current = null
                              handleDialogOpen(e)
                            }}
                          >
                            <DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    )
                  }}
                />
              </Datagrid>
            </Box>
          </Scrollbar>
        )}
      </CardContent>
      
      {states.selectedServer &&
        <CreateOrEdit resource={resource} basePath='' record={states.editingRule} >
          <OwnershipRuleDialog
            open={states.dialogOpen}
            rule={states.editingRule}
            client={states.selectedServer}
            onClose={handleEditDialogClose}
            dataOwnerGroupOptions={dataOwnerGroupOptions}
          />
        </CreateOrEdit>}
      <Confirm
        isOpen={delConfirmOpen}
        loading={delSaving}
        title={"Delete Ownership Rule"}
        onConfirm={handleDelete}
        onClose={(e:any) => {
          handleDeleteDialogClose(e)
          setStates(v => {
            const v1 = ({...v, deletingRule: null})
            tempDelRec.current = v.deletingRule
            return v1
          })
          setTimeout(() => { tempDelRec.current = null }, 5000)
        }}
        content={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon fontSize="large" sx={{ color: 'info.main' }}/>
            <Typography
              component="span"
              variant="body2"
              sx={{ ml: 1 }}
            >
              Please confirm whether to proceed with the deletion of this
              {` "#${(states.deletingRule ?? tempDelRec.current)?.priority}" `}
              Ownership Rule.
            </Typography>
          </Box>
        }
      />
    </Card>
  );
};

export default OwnershipTab;
